/**
 * The recorded turns — GENERATED, do not hand-edit.
 *
 * Produced by ~/march-demo-data/build-fixture.py from the SSE streams captured
 * when these five questions were asked against the live dev agent. The answer
 * text, the structured cards, the follow-up chips, the retrieved sources and
 * the timings are all the product's own output, embedded verbatim: re-typing
 * them is how a demo ends up quoting something the system never said.
 *
 * To refresh: re-run ~/march-demo-pull.sh, then build-fixture.py.
 */

/* eslint-disable */
export interface GeneratedTurn {
  id: string;
  question: string;
  answer: string;
  intent_type?: string | null;
  tools_used: string[];
  processing_time_ms?: number | null;
  thinking_event_count: number;
  suggestions: string[];
  structured?: Record<string, unknown>;
  rag_sources?: Array<Record<string, unknown>>;
  guideline_evidence?: Record<string, unknown>;
  specialty_outputs?: Array<Record<string, unknown>>;
  engaged_specialties?: string[];
  router_overview?: string;
}

export const GENERATED_TURNS: GeneratedTurn[] = [
  {
    "id": "what-do-i-need-to-know-before-i-see-her",
    "question": "What do I need to know before I see her?",
    "answer": "Normal pregnancy and peripartum cardiomyopathy.\n\nClinical Snapshot:\n- Normal pregnancy and peripartum cardiomyopathy\n- Normal pregnancy; onset 2026-08-21\n- Peripartum cardiomyopathy; onset 2023-10-14\n- Preeclampsia; onset 2023-09-15\n- Chronic kidney disease stage 3A; onset 2023-11-24\n- Pulmonary emphysema; onset 2024-12-20\n- Obesity; onset 2024-12-20\n- Chronic viral hepatitis C; onset 2023-03-03\n- Dependent drug abuse; onset 2003-07-06\n- Chronic intractable migraine without aura; onset 2002-01-17\n- Chronic pain; onset 2002-04-15\n\nActive Medication:\n- Acetaminophen 300 MG / Hydrocodone Bitartrate 5 MG; oral tablet; every 6 hours as needed for pain\n- Ibuprofen 600 MG; oral tablet; every 8 hours as needed for pain\n- Alprazolam 0.5 MG; oral tablet; three times daily as needed for anxiety\n- Topiramate 50 MG; oral tablet; twice daily\n- Lisinopril 10 MG; oral tablet; once daily\n- Metoprolol succinate 50 MG; extended release oral tablet; once daily\n\nRecent Care Activity:\n- Prenatal initial visit; 2026-08-21\n- Routine antenatal care; started 2026-08-21\n- Ultrasound scan for fetal viability; 2026-08-21\n- Urinalysis, protein, qualitative; 2026-08-21\n- Urine culture; 2026-08-21\n- Urine screening test for diabetes; 2026-08-21\n- Encounter for problem; 2026-07-24\n- Encounter for problem; 2026-06-24\n- Encounter for problem; 2026-05-25\n\nNotifications:\n- No allergies or intolerances are documented in the chart\n\nNo allergies are recorded for this patient.\nNo family history is recorded for this patient.",
    "intent_type": "chart_briefing",
    "tools_used": [
      "get_conditions",
      "get_medications",
      "get_allergies",
      "get_observations",
      "get_encounters",
      "get_procedures",
      "get_care_plans",
      "get_diagnostic_reports",
      "get_family_history"
    ],
    "processing_time_ms": 16996,
    "thinking_event_count": 55,
    "suggestions": [
      "What changed since her last visit?",
      "Any care gaps I should address?",
      "Summarize her active medications"
    ]
  },
  {
    "id": "what-care-gaps-should-i-address-for-this",
    "question": "What care gaps should I address for this patient?",
    "answer": "**Primary Care lead-specialty review**\n\nThe patient requires several outstanding diagnostic assessments including Hepatitis C virus RNA quantification, Hepatitis C genotype testing, liver function tests, and a urine albumin-to-creatinine ratio. Management must also address the need for an echocardiogram, pulmonary function tests, and screenings for depression, anxiety, and substance use disorder. These gaps must be addressed to complete the patient's clinical evaluation.",
    "intent_type": "clinical_reasoning",
    "tools_used": [
      "cma_route"
    ],
    "processing_time_ms": 12752,
    "thinking_event_count": 81,
    "suggestions": [
      "Should we order a UACR and kidney ultrasound to further stage her CKD 3A?",
      "Given her peripartum cardiomyopathy, should we obtain a new echocardiogram?",
      "Should we order liver function tests and HCV RNA to assess her hepatitis C?"
    ],
    "structured": {
      "response_type": "flexible",
      "title": "Primary Care lead-specialty review",
      "sections": [
        {
          "type": "list",
          "title": "Demographic & Physiologic Considerations",
          "items": [
            "Age: 38 years old; age-related decline in renal reserve is expected, potentially exacerbating her chronic kidney disease stage 3A.",
            "Sex: Female; increased risk for cardiovascular disease post-menopause, though currently premenopausal or perimenopausal, and typical hormonal state involves regular menstrual cycles and estrogen production."
          ],
          "panel": "patient_summary"
        },
        {
          "type": "nested_list",
          "title": "Medical History",
          "items": [
            {
              "title": "Active conditions",
              "children": [
                "Body mass index 30+ - obesity",
                "Chronic intractable migraine without aura",
                "Chronic kidney disease stage 3A",
                "Chronic pain",
                "Chronic viral hepatitis C",
                "Dependent drug abuse",
                "Impacted molars",
                "Peripartum cardiomyopathy",
                "Preeclampsia",
                "Pulmonary emphysema"
              ],
              "count": 10,
              "columns": 2
            },
            {
              "title": "Past medical history",
              "children": [
                "Gingivitis (resolved 2018-12-28, onset 2018-12-14)",
                "Anemia (resolved 2014-06-27, onset 2013-11-22)",
                "Viral sinusitis (resolved 2010-04-13, onset 2010-03-27)",
                "Leaking dental filling (resolved 2010-04-09, onset 2010-04-09)",
                "Medication review due (resolved 2010-03-26, onset 2004-11-19)",
                "Normal pregnancy (resolved 2009-12-28, onset 2009-12-11)",
                "Streptococcal sore throat (resolved 2009-07-05, onset 2009-06-21)",
                "Acute infective cystitis (resolved 2007-02-12, onset 2007-02-10)",
                "Overdose (resolved 2006-10-03, onset 2006-10-01)",
                "Infection of tooth (resolved 2004-12-03, onset 2004-12-03)",
                "Acute bronchitis (resolved 2000-03-17, onset 2000-02-27)",
                "Primary dental caries (resolved 1996-10-11, onset 1996-10-11)",
                "Traumatic dislocation of temporomandibular joint (resolved 1994-06-25, onset 1994-05-04)",
                "Gingival disease (resolved 1994-05-13, onset 1994-05-13)",
                "Acute viral pharyngitis (resolved 1994-05-08, onset 1994-04-29)",
                "Sprain of ankle (resolved 1992-07-25, onset 1992-07-03)",
                "Sprain (resolved 1992-07-25, onset 1992-07-03)",
                "Otitis media (resolved 1991-03-08, onset 1990-09-28)"
              ],
              "count": 18,
              "columns": 2
            },
            {
              "title": "Family history: none recorded",
              "children": []
            },
            {
              "title": "Allergies: none recorded",
              "children": []
            },
            {
              "title": "Trends (past 2 years): Pain severity decreased",
              "children": []
            },
            {
              "title": "Current medications",
              "children": [
                "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain)",
                "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain)",
                "Alprazolam 0.5 MG Oral Tablet (active, Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety., @ 2026-03-26, for Severe anxiety (panic))",
                "topiramate 50 MG Oral Tablet (active, Take 1 tablet (50 mg) by mouth twice daily., @ 2024-01-25, for Chronic intractable migraine without aura)",
                "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy)",
                "24 HR metoprolol succinate 50 MG Extended Release Oral Tablet (active, Take 1 tablet (50 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy)"
              ],
              "count": 6,
              "columns": 2
            }
          ],
          "panel": "patient_summary"
        },
        {
          "type": "list",
          "title": "Organ Health",
          "items": [
            "Heart: moderate function with moderate risk, assessment rests on thin or dated evidence; risk raised by obesity and borderline LDL cholesterol, lowered by blood pressure below 120/80 and never smoking; no NT-proBNP, HbA1c, weekly physical activity, diet quality, or alcohol intake on record.",
            "Kidney: moderate function with moderate risk, assessment rests on thin or dated evidence; risk raised by obesity and occasional nephrotoxin exposure, lowered by blood pressure below 120/80 and never smoking; no UACR, HbA1c, dietary sodium and protein load, weekly physical activity, or family history of kidney disease on record."
          ],
          "panel": "patient_summary",
          "itemGlyphs": [
            "unknown",
            "unknown"
          ]
        },
        {
          "type": "nested_list",
          "title": "Social Determinants of Health (SDOH)",
          "items": [
            {
              "title": "",
              "children": [
                "Utility security: barrier — Inadequate housing utilities -- limits ability to maintain stable health environment.; screening stale",
                "Psychological stress: barrier — Severe anxiety (panic) -- complicates management of chronic conditions and adherence to care.; screening stale",
                "Education: barrier — Only received primary school education -- may impact health literacy and self-management skills.; screening stale",
                "Interpersonal safety: screened negative — no current safety concerns identified.; screening stale",
                "Employment: screened negative — provides financial stability but may impact healthcare access.; screening stale"
              ],
              "childGlyphs": [
                "barrier",
                "barrier",
                "barrier",
                "screened_negative",
                "screened_negative"
              ]
            }
          ],
          "panel": "patient_summary"
        },
        {
          "type": "list",
          "title": "Care Gaps",
          "items": [
            "Hepatitis C virus RNA quantification",
            "Hepatitis C genotype testing",
            "Liver function tests",
            "Urine albumin-to-creatinine ratio",
            "Echocardiogram",
            "Pulmonary function tests",
            "Screen for depression",
            "Screen for anxiety",
            "Screen for substance use disorder",
            "Not documented on the chart: Family history, Smoking history, HbA1c, Weekly physical activity, Diet quality, Alcohol intake, NT-proBNP, Kidney ultrasound, Medications for chronic kidney disease, Medications for peripartum cardiomyopathy, Medications for pulmonary emphysema, and Dental radiographs."
          ]
        }
      ],
      "presentation": {
        "density": "comfortable",
        "tone": "clinical"
      },
      "meta": {
        "render_hint": "cma",
        "response_shape": "focused",
        "protocol_version": "structured-output-v2",
        "section_count": 5,
        "preserve_section_titles": true,
        "tools_used": [
          "cma_route"
        ],
        "adk_clinical_facts_panel": true,
        "adk_facts_panel_title": "Facts & Evidences",
        "adk_patient_summary_panel_title": "Patient Summary",
        "adk_cma_sections_unrendered": []
      }
    },
    "guideline_evidence": {
      "categories": [
        {
          "category": "care_gaps",
          "answer": "",
          "evidence_quality": "strong",
          "source_numbers": [
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8"
          ]
        }
      ],
      "references": [
        {
          "doc_title": "Hepatitis C Guidance 2023 Update - AASLD-IDSA Recommendations for Testing, Managing, and Treating Hepatitis C Virus Infection (2023)",
          "document_type": "guideline",
          "passages": [
            {
              "category": "care_gaps",
              "number": "1",
              "snippet": "This update focuses on changes to the guidance since the previous 2020 published update, including ongoing emphasis on recommended universal screening; management recommendations for incomplete treatment adherence; expanded eligibility for simplified chronic HCV infection treatme",
              "passage_text": "This update focuses on changes to the guidance since the previous 2020 published update, including ongoing emphasis on recommended universal screening; management recommendations for incomplete treatment adherence; expanded eligibility for simplified chronic HCV infection treatment in adults with minimal monitoring; updated treatment and retreatment recommendations for children as young as 3 years; management and treatment recommendations in the transplantation setting; and screening, treatment, and management recommendations for unique and key populations. Keywords. HCV screening; direct-acting antivirals; HCV guidance; HCV treatment; HCV prevention. The Infectious Diseases Society of America (IDSA) and the American Association for the Study of Liver Diseases (AASLD) collaboratively initiated the Hepatitis C Virus (HCV) Guidance Project in 2013 to provide clinicians with evidence-based, unbiased, timely guidance regarding diagnosis, treatment, and management of HCV infection. The project includes the web-based HCV guidance platform (www. hcvguidelines.org) to enable rapid, accessible dissemination",
              "document_type": "guideline",
              "section": "Hepatitis C Guidance 2023 Update - AASLD-IDSA Recommendations for Testing, Managing, and Treating Hepatitis C Virus Infection (2023) > p. 1",
              "page_start": 1,
              "page_end": 1
            },
            {
              "category": "care_gaps",
              "number": "4",
              "snippet": "screening, management, and treatment recommendations for unique and key populations. In addition, we highlight key issues critical to HCV management with the mission of HCV elimination in mind. See Figure 1 for key points in this HCV guidance update. PROCESS The HCV guidance was ",
              "passage_text": "screening, management, and treatment recommendations for unique and key populations. In addition, we highlight key issues critical to HCV management with the mission of HCV elimination in mind. See Figure 1 for key points in this HCV guidance update. PROCESS The HCV guidance was developed and is regularly updated by a volunteer panel of more than 30 infectious diseases and hepatology clinicians and investigators with HCV expertise representing IDSA and AASLD, respectively. Four co-chairs (2 from each society) oversee the work of the Guidance Panel. The HCV guidance undergoes major biannual updates based on a rigorous literature review that encompasses peerreviewed, published literature and relevant abstracts from national and international scientific conferences. The data are reviewed by section leads, with points of discussion resolved during section and full panel remote meetings. 2 • CID • Bhattacharya et al New or updated recommendations are evaluated using a",
              "document_type": "guideline",
              "section": "Hepatitis C Guidance 2023 Update - AASLD-IDSA Recommendations for Testing, Managing, and Treating Hepatitis C Virus Infection (2023) > p. 2",
              "page_start": 2,
              "page_end": 2
            }
          ]
        },
        {
          "doc_title": "European Respiratory Society Clinical Practice Guideline for the Management of Adult Bronchiectasis (2025)",
          "document_type": "guideline",
          "passages": [
            {
              "category": "care_gaps",
              "number": "7",
              "snippet": "Monitoring and evaluation should focus on early identification and timely intervention for patients experiencing disease deterioration, as this is a common feature of bronchiectasis. Regular clinical assessment, symptom tracking, and objective investigations should be prioritized",
              "passage_text": "Monitoring and evaluation should focus on early identification and timely intervention for patients experiencing disease deterioration, as this is a common feature of bronchiectasis. Regular clinical assessment, symptom tracking, and objective investigations should be prioritized to detect worsening conditions and guide appropriate treatment. Key aspects of monitoring include evaluating exacerbation frequency, respiratory function decline, increased need for oxygen or ventilatory support, and persistent infections Research priorities Future research should focus on: 1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 - Improving diagnostic tools to enable faster identification, severity assessment, and objective follow-up of deteriorating patients with bronchiectasis. - Determining the optimal timepoint for hospitalisation referral, as well as referral for surgery or lung transplantation. - Establishing strategies for measure end-of-life care and palliative management in patients with advanced bronchiectasis.",
              "document_type": "guideline",
              "section": "European Respiratory Society Clinical Practice Guideline for the Management of Adult Bronchiectasis (2025) > p. 185",
              "page_start": 185,
              "page_end": 185
            }
          ]
        },
        {
          "doc_title": "Standards of Care in Diabetes 2026 - 11. Chronic Kidney Disease and Risk Management (2026)",
          "document_type": "guideline",
          "passages": [
            {
              "category": "care_gaps",
              "number": "8",
              "snippet": "S246 Diabetes Care Volume 49, Supplement 1, January 2026 11. CHRONIC KIDNEY DISEASE AND RISK MANAGEMENT 11. Chronic Kidney Disease and Risk Management: Standards of Care in Diabetes—2026 American Diabetes Association Professional Practice Committee for Diabetes* Diabetes Care 202",
              "passage_text": "S246 Diabetes Care Volume 49, Supplement 1, January 2026 11. CHRONIC KIDNEY DISEASE AND RISK MANAGEMENT 11. Chronic Kidney Disease and Risk Management: Standards of Care in Diabetes—2026 American Diabetes Association Professional Practice Committee for Diabetes* Diabetes Care 2026;49(Suppl. 1):S246–S260 | https://doi.org/10.2337/dc26-S011 The American Diabetes Association (ADA) “Standards of Care in Diabetes” in­ cludes the ADA’s current clinical practice recommendations and is intended to provide the components of diabetes care, general treatment goals and guide­ lines, and tools to evaluate quality of care. Members of the ADA Professional Practice Committee for Diabetes, an interprofessional expert committee, are re­ sponsible for updating the Standards of Care annually, or more frequently as war­ ranted. For a detailed description of ADA standards, statements, and reports, as well as the evidence-grading system for ADA’s clinical practice recommendations and a full list of Professional Practice Committee members, please refer to Introduction and Methodology. Readers who wish to comment on the Standards of Care are invited to do so at professional.diabetes.org/SOC. For prevention",
              "document_type": "guideline",
              "section": "Standards of Care in Diabetes 2026 - 11. Chronic Kidney Disease and Risk Management (2026) > p. 1",
              "page_start": 1,
              "page_end": 1
            }
          ]
        },
        {
          "doc_title": "Goldman-Cecil Medicine, 2-Volume Set, 27th Edition (2023)",
          "document_type": "textbook",
          "passages": [
            {
              "category": "care_gaps",
              "number": "2",
              "snippet": "TREATMENT General Principles Management, which is based on the cause of chronic kidney disease and its stage, should begin as soon as the disease is detected. Cause-specific therapy that is directed at factors that cause chronic kidney disease may induce remission and slow progre",
              "passage_text": "TREATMENT General Principles Management, which is based on the cause of chronic kidney disease and its stage, should begin as soon as the disease is detected. Cause-specific therapy that is directed at factors that cause chronic kidney disease may induce remission and slow progression. Stage-based therapies, which are directed by the level of albuminuria and the glomerular filtration rate, aim to slow the progression and prevent complications of chronic kidney disease, irrespective of the cause (see Table 116-2). Stage-based recommendations are cumulative, so recommendations for late stages include recommendations for early stages. Although many common problems can be managed with existing guidelines by non-nephrologists, specialty nephrology referral is recommended for patients with an uncertain diagnosis, an albumin to creatinine ratio greater than 300 mg/g (stage A3), a glomerular filtration rate less than 30 mL/min/1.73 m2 (stage G4 to G5), or complications that are difficult to manage.",
              "document_type": "textbook",
              "section": "Goldman-Cecil Medicine, 2-Volume Set, 27th Edition (2023) > p. 1339",
              "page_start": 1339,
              "page_end": 1339
            }
          ]
        },
        {
          "doc_title": "Brenner and Rector's The Kidney, 11th Edition (2019)",
          "document_type": "textbook",
          "passages": [
            {
              "category": "care_gaps",
              "number": "3",
              "snippet": "CLASSIFICATION OF CHRONIC KIDNEY DISEASE The original classification proposed in 2002 subdivided CKD into five stages according to the GFR, reflecting the observation that in the majority of cases, CKD progresses slowly through the stages before reaching end-stage kidney disease ",
              "passage_text": "CLASSIFICATION OF CHRONIC KIDNEY DISEASE The original classification proposed in 2002 subdivided CKD into five stages according to the GFR, reflecting the observation that in the majority of cases, CKD progresses slowly through the stages before reaching end-stage kidney disease (ESKD). In 2008, a National Institute for Health and Care Excellence (NICE) guideline proposed that stage 3 (GFR 59–30 mL/min/1.73 m2) be subdivided into stages 3a (GFR 59–45 mL/min/1.73 m2) and 3b (GFR 44–30 mL/min/1.73 m2), reflecting a broad consensus that there are important differences in clinical aspects and prognosis between these groups. The CKD staging system has two important implications: First, it suggests that if CKD is detected at an early stage, intervention may be possible to prevent or slow progression to more advanced stages.4 Second, it reflects the observation that as GFR declines, the risk profile of persons and associated complications changes. Thus the staging system provides a useful framework for structuring therapy and prioritizing interventions to produce a comprehensive strategy for the management of CKD. Although universally adopted, this classification system was limited by not",
              "document_type": "textbook",
              "section": "Brenner and Rector's The Kidney, 11th Edition (2019) > p. 2581",
              "page_start": 2581,
              "page_end": 2581
            }
          ]
        },
        {
          "doc_title": "Maternal-Fetal and Neonatal Endocrinology - Physiology, Pathophysiology, and Clinical Management (2020)",
          "document_type": "textbook",
          "passages": [
            {
              "category": "care_gaps",
              "number": "5",
              "snippet": "ANTENATAL MANAGEMENT OF PREECLAMPSIA As in gestational and chronic hypertension, the challenges of management in preeclampsia lie in balancing the maternal and fetal risks of continuing pregnancy (expectant management) against the maternal risks of intervention and the fetal risk",
              "passage_text": "ANTENATAL MANAGEMENT OF PREECLAMPSIA As in gestational and chronic hypertension, the challenges of management in preeclampsia lie in balancing the maternal and fetal risks of continuing pregnancy (expectant management) against the maternal risks of intervention and the fetal risks of 472 SECTION 1 The Mother preterm delivery (interventionist management). Women with preeclampsia are at greater risk of sudden deterioration and serious complications, and management protocols for preeclampsia take this into account. It is important to recognize that, although most guidelines include specific gestational cutoffs for expectant and interventional management, a continuum of risk exists. While opting to continue the pregnancy, the goals of treatment are to maintain a safe blood pressure and monitor the mother for deteriorating disease and the fetus for signs of placental dysfunction and growth restriction. 26.8.1 Inpatient or Outpatient Management? Most international bodies recommend hospital admission at the point of diagnosis in order to complete a full maternal and fetal assessment, including monitoring of blood pressure over a 24-h period.64,72,73,121 Many women ultimately will be suita",
              "document_type": "textbook",
              "section": "Maternal-Fetal and Neonatal Endocrinology - Physiology, Pathophysiology, and Clinical Management (2020) > pp. 478-479",
              "page_start": 478,
              "page_end": 479
            },
            {
              "category": "care_gaps",
              "number": "6",
              "snippet": "CONCLUSION l Hypertension in pregnancy is common and extensively investigated, but the precise mechanisms of disease remain unclear. l Endothelial dysfunction with a background of disrupted maternal hemodynamics, angiogenic balance, lipid and glucose metabolism, and hormonal sign",
              "passage_text": "CONCLUSION l Hypertension in pregnancy is common and extensively investigated, but the precise mechanisms of disease remain unclear. l Endothelial dysfunction with a background of disrupted maternal hemodynamics, angiogenic balance, lipid and glucose metabolism, and hormonal signaling is typical of hypertension in pregnancy. l The risk of HDP can be reduced with administration of low-dose aspirin from the first trimester in high-risk women. l Models combining maternal characteristics, uterine artery Dopplers, and biomarkers can screen effectively for women at high risk of severe and early-onset preeclampsia. l New technology in validated automated devices for home monitoring of blood pressure, together with digital distance monitoring from the hospital team, can reduce costs and improve women’s experience of antenatal monitoring. l The diagnosis of preeclampsia can be clarified by the use of novel tests including PlGF and the sFlt-1/PlGF ratio. l Models predicting the development of maternal complications can be used to address the clinical conundrum of timing of delivery in preterm preeclampsia. l The postnatal period is a critical time for monitoring and prevention of late compli",
              "document_type": "textbook",
              "section": "Maternal-Fetal and Neonatal Endocrinology - Physiology, Pathophysiology, and Clinical Management (2020) > p. 485",
              "page_start": 485,
              "page_end": 485
            }
          ]
        }
      ],
      "review_incomplete": false,
      "retrieval_quality": {},
      "uncited_retrieval_disclosure": "6 guideline source(s) were retrieved for this review. The reasoning above does not cite any of them directly, so treat these as background reading rather than as the stated basis for the conclusions above."
    }
  },
  {
    "id": "are-any-of-her-current-medications-unsaf",
    "question": "Are any of her current medications unsafe in this pregnancy?",
    "answer": "**Pharmacology lead-specialty review**\n\nYes, multiple current medications pose safety concerns in this pregnancy.\n**Label findings**\n- Respiratory depression risk: Concomitant use of Acetaminophen / HYDROcodone Bitartrate and [Alprazolam](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=308048) increases the risk of respiratory depression, coma, and death; limit dosage and duration, and monitor closely for respiratory depression and sedation.\n- Hepatic toxicity risk: Acetaminophen carries a boxed warning for potential acute liver failure, especially with doses exceeding 4000 mg/day or in patients with underlying liver disease; monitor liver function.\n- Renal function risk: Co-administration of [Ibuprofen](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=197806) and [Lisinopril](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=314076) may result in deterioration of renal function, including possible acute renal failure, especially in patients with compromised renal function; monitor renal function periodically.\n- Blood pressure risk: NSAIDs like [Ibuprofen](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=197806) may diminish the antihypertensive effect of ACE inhibitors like [Lisinopril](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=314076); monitor blood pressure to ensure the desired blood pressure is obtained.\n- Opioid withdrawal risk: Concomitant use of Acetaminophen / HYDROcodone Bitartrate and [topiramate](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=151226), a CYP3A4 inducer, can decrease hydrocodone plasma concentration, potentially leading to decreased efficacy or withdrawal syndrome; consider increasing the hydrocodone dosage if concomitant use is necessary.\n- Diuretic efficacy risk: Opioids like Acetaminophen / HYDROcodone Bitartrate can reduce the efficacy of diuretics like [Lisinopril](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=314076); evaluate for signs of diminished diuresis and/or effects on blood pressure and increase the diuretic dosage as needed.\n- Angioedema risk: [Lisinopril](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=314076) carries a boxed warning for fetal toxicity when used in the second and third trimesters of pregnancy; discontinue as soon as possible.\n- Hypotension risk: Concomitant use of Acetaminophen / HYDROcodone Bitartrate and [Alprazolam](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=308048) can increase the risk of hypotension; monitor blood pressure.\n- Abuse/dependence risk: Acetaminophen / HYDROcodone Bitartrate carries a boxed warning for abuse, misuse, and addiction; monitor for signs of misuse.\n- Bradycardia risk: Metoprolol succinate carries a boxed warning regarding abrupt cessation potentially exacerbating ischemic heart disease; do not stop abruptly.\n**Evidence review**\n- The patient's chronic kidney disease stage 3A (diagnosed 2023-11-24) increases the risk of renal deterioration from concomitant use of [Ibuprofen](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=197806) and [Lisinopril](https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=314076).\n- The patient's history of dependent drug abuse (diagnosed 2003-07-06) increases the risk associated with Acetaminophen / HYDROcodone Bitartrate [1].\n- The patient's chronic viral hepatitis C (diagnosed 2023-03-03) increases the risk of hepatotoxicity from Acetaminophen [2].\nThe panel below carries the full label record (96 findings).",
    "intent_type": "clinical_reasoning",
    "tools_used": [
      "cma_route"
    ],
    "processing_time_ms": 111639,
    "thinking_event_count": 62,
    "suggestions": [
      "What are the most recent serum creatinine and GFR levels for this patient?",
      "Should lisinopril be discontinued given the risk of fetal renal toxicity?",
      "Is topiramate contraindicated for this patient's current pregnancy status?"
    ],
    "structured": {
      "response_type": "flexible",
      "title": "Pharmacology lead-specialty review",
      "sections": [
        {
          "type": "list",
          "title": "Demographic & Physiologic Considerations",
          "items": [
            "Age: 38 years old; age-related decline in renal reserve is expected, potentially exacerbating her chronic kidney disease stage 3A.",
            "Sex: Female; increased risk for cardiovascular disease post-menopause, though currently premenopausal or perimenopausal, and typical hormonal state involves regular menstrual cycles and estrogen production."
          ],
          "panel": "patient_summary"
        },
        {
          "type": "nested_list",
          "title": "Medical History",
          "items": [
            {
              "title": "Active conditions",
              "children": [
                "Body mass index 30+ - obesity",
                "Chronic intractable migraine without aura",
                "Chronic kidney disease stage 3A",
                "Chronic pain",
                "Chronic viral hepatitis C",
                "Dependent drug abuse",
                "Impacted molars",
                "Peripartum cardiomyopathy",
                "Preeclampsia",
                "Pulmonary emphysema"
              ],
              "count": 10,
              "columns": 2
            },
            {
              "title": "Past medical history",
              "children": [
                "Gingivitis (resolved 2018-12-28, onset 2018-12-14)",
                "Anemia (resolved 2014-06-27, onset 2013-11-22)",
                "Viral sinusitis (resolved 2010-04-13, onset 2010-03-27)",
                "Leaking dental filling (resolved 2010-04-09, onset 2010-04-09)",
                "Medication review due (resolved 2010-03-26, onset 2004-11-19)",
                "Normal pregnancy (resolved 2009-12-28, onset 2009-12-11)",
                "Streptococcal sore throat (resolved 2009-07-05, onset 2009-06-21)",
                "Acute infective cystitis (resolved 2007-02-12, onset 2007-02-10)",
                "Overdose (resolved 2006-10-03, onset 2006-10-01)",
                "Infection of tooth (resolved 2004-12-03, onset 2004-12-03)",
                "Acute bronchitis (resolved 2000-03-17, onset 2000-02-27)",
                "Primary dental caries (resolved 1996-10-11, onset 1996-10-11)",
                "Traumatic dislocation of temporomandibular joint (resolved 1994-06-25, onset 1994-05-04)",
                "Gingival disease (resolved 1994-05-13, onset 1994-05-13)",
                "Acute viral pharyngitis (resolved 1994-05-08, onset 1994-04-29)",
                "Sprain of ankle (resolved 1992-07-25, onset 1992-07-03)",
                "Sprain (resolved 1992-07-25, onset 1992-07-03)",
                "Otitis media (resolved 1991-03-08, onset 1990-09-28)"
              ],
              "count": 18,
              "columns": 2
            },
            {
              "title": "Family history: none recorded",
              "children": []
            },
            {
              "title": "Allergies: none recorded",
              "children": []
            },
            {
              "title": "Trends (past 2 years): Pain severity decreased",
              "children": []
            },
            {
              "title": "Current medications",
              "children": [
                "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain)",
                "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain)",
                "Alprazolam 0.5 MG Oral Tablet (active, Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety., @ 2026-03-26, for Severe anxiety (panic))",
                "topiramate 50 MG Oral Tablet (active, Take 1 tablet (50 mg) by mouth twice daily., @ 2024-01-25, for Chronic intractable migraine without aura)",
                "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy)",
                "24 HR metoprolol succinate 50 MG Extended Release Oral Tablet (active, Take 1 tablet (50 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy)"
              ],
              "count": 6,
              "columns": 2
            }
          ],
          "panel": "patient_summary"
        },
        {
          "type": "list",
          "title": "Organ Health",
          "items": [
            "Heart: moderate function with moderate risk, assessment rests on thin or dated evidence; risk raised by obesity and borderline LDL cholesterol, lowered by blood pressure below 120/80 and never smoking; no NT-proBNP, HbA1c, weekly physical activity, diet quality, or alcohol intake on record.",
            "Kidney: moderate function with moderate risk, assessment rests on thin or dated evidence; risk raised by obesity and occasional nephrotoxin exposure, lowered by blood pressure below 120/80 and never smoking; no UACR, HbA1c, dietary sodium and protein load, weekly physical activity, or family history of kidney disease on record."
          ],
          "panel": "patient_summary",
          "itemGlyphs": [
            "unknown",
            "unknown"
          ]
        },
        {
          "type": "nested_list",
          "title": "Social Determinants of Health (SDOH)",
          "items": [
            {
              "title": "",
              "children": [
                "Utility security: barrier — Inadequate housing utilities -- limits ability to maintain stable health environment.; screening stale",
                "Psychological stress: barrier — Severe anxiety (panic) -- complicates management of chronic conditions and adherence to care.; screening stale",
                "Education: barrier — Only received primary school education -- may impact health literacy and self-management skills.; screening stale",
                "Interpersonal safety: screened negative — no current safety concerns identified.; screening stale",
                "Employment: screened negative — provides financial stability but may impact healthcare access.; screening stale"
              ],
              "childGlyphs": [
                "barrier",
                "barrier",
                "barrier",
                "screened_negative",
                "screened_negative"
              ]
            }
          ],
          "panel": "patient_summary"
        },
        {
          "type": "dailymed_panel",
          "title": "",
          "data": {
            "sections": [
              {
                "severity": "AVOID",
                "label": "Avoid",
                "items": []
              },
              {
                "severity": "MONITOR",
                "label": "Monitor",
                "items": [
                  {
                    "action": "Reserve concomitant prescribing of these drugs for use in patients for whom alternative treatment ...",
                    "reasonings": [
                      "Reserve concomitant prescribing of these drugs for use in patients for whom alternative treatment options are inadequate and consider prescribing an opioid overdose reversal agent if concomitant use is warranted.",
                      "Due to additive pharmacologic effect, the concomitant use of benzodiazepines and other CNS depressants, such as benzodiazepines and other sedative hypnotics, anxiolytics, and tranquilizers, muscle relaxants, general anesthetics, antipsychotics, gabapentinoids (gabapentin or pregabalin), and other opioids, including alcohol, can increase the risk of hypotension, respiratory depression, profound sedation, coma, and death."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain) [RXNORM:856987]",
                        "drug_b": "Alprazolam 0.5 MG Oral Tablet (active, Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety., @ 2026-03-26, for Severe anxiety (panic)) [RXNORM:308048]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "If concomitant use is necessary, consider increasing the Hydrocodone Bitartrate and Acetaminophen Oral Solution ...",
                    "reasonings": [
                      "If concomitant use is necessary, consider increasing the Hydrocodone Bitartrate and Acetaminophen Oral Solution dosage until stable drug effects are achieved.",
                      "The concomitant use of Hydrocodone Bitartrate and Acetaminophen Oral Solution and CYP3A4 inducers, such as rifampin, carbamazepine, and phenytoin, can decrease the plasma concentration of hydrocodone, resulting in decreased efficacy or onset of a withdrawal syndrome in patients who have developed physical dependence to Hydrocodone Bitartrate and Acetaminophen Oral Solution.",
                      "The concomitant use of Hydrocodone Bitartrate and Acetaminophen Tablets and CYP3A4 inducers, such as rifampin, carbamazepine, and phenytoin, can decrease the plasma concentration of Hydrocodone Bitartrate and Acetaminophen Tablets, resulting in decreased efficacy or onset of a withdrawal syndrome in patients who have developed physical dependence to Hydrocodone Bitartrate and Acetaminophen Tablets."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain) [RXNORM:856987]",
                        "drug_b": "topiramate 50 MG Oral Tablet (active, Take 1 tablet (50 mg) by mouth twice daily., @ 2024-01-25, for Chronic intractable migraine without aura) [RXNORM:151226]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "If concomitant use is warranted, evaluate patients for signs of diminished diuresis and/or effects ...",
                    "reasonings": [
                      "If concomitant use is warranted, evaluate patients for signs of diminished diuresis and/or effects on blood pressure and increase the dosage of the diuretic as needed.",
                      "Opioids can reduce the efficacy of diuretics by inducing the release of antidiuretic hormone."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain) [RXNORM:856987]",
                        "drug_b": "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:314076]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "This interaction should be given consideration in patients taking NSAIDs concomitantly with ACE-inhibitors.",
                    "reasonings": [
                      "Reports suggest that NSAIDs may diminish the antihypertensive effect of ACE-inhibitors."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain) [RXNORM:197806]",
                        "drug_b": "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:314076]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "During concomitant use of CALDOLOR with diuretics, observe patients for signs of worsening renal ...",
                    "reasonings": [
                      "During concomitant use of CALDOLOR with diuretics, observe patients for signs of worsening renal function, in addition to assuring diuretic efficacy including antihypertensive effects.",
                      "Clinical studies, as well as post-marketing observations, showed that NSAIDs reduced the natriuretic effect of loop diuretics (e.g., furosemide) and thiazide diuretics in some patients."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain) [RXNORM:197806]",
                        "drug_b": "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:314076]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "During concomitant use of CALDOLOR and ACE-inhibitors, ARBs, or beta- blockers, monitor blood pressure ...",
                    "reasonings": [
                      "During concomitant use of CALDOLOR and ACE-inhibitors, ARBs, or beta- blockers, monitor blood pressure to ensure that the desired blood pressure is obtained.",
                      "NSAIDs may diminish the antihypertensive effect of angiotensin converting enzyme (ACE) inhibitors, angiotensin receptor blockers (ARBs), or beta-blockers (including propranolol)."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain) [RXNORM:197806]",
                        "drug_b": "24 HR metoprolol succinate 50 MG Extended Release Oral Tablet (active, Take 1 tablet (50 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:866436]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "Limit dosage and duration of concomitant use of alprazolam extended-release tablets and opioids, and ...",
                    "reasonings": [
                      "Limit dosage and duration of concomitant use of alprazolam extended-release tablets and opioids, and monitor patients closely for respiratory depression and sedation.",
                      "The concomitant use of benzodiazepines and opioids increases the risk of respiratory depression because of actions at different receptor sites in the CNS that control respiration."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Alprazolam 0.5 MG Oral Tablet (active, Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety., @ 2026-03-26, for Severe anxiety (panic)) [RXNORM:308048]",
                        "drug_b": "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain) [RXNORM:856987]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "Limit dosage and duration of concomitant use of benzodiazepines and opioids, and monitor patients ...",
                    "reasonings": [
                      "Limit dosage and duration of concomitant use of benzodiazepines and opioids, and monitor patients closely for respiratory depression and sedation.",
                      "The concomitant use of benzodiazepines and opioids increases the risk of respiratory depression because of actions at different receptor sites in the CNS that control respiration."
                    ],
                    "pairs": [
                      {
                        "drug_a": "Alprazolam 0.5 MG Oral Tablet (active, Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety., @ 2026-03-26, for Severe anxiety (panic)) [RXNORM:308048]",
                        "drug_b": "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain) [RXNORM:856987]"
                      }
                    ],
                    "grade": "Monitor"
                  },
                  {
                    "action": "Monitor renal function periodically in patients receiving lisinopril and NSAID therapy.",
                    "reasonings": [
                      "Co-administration of NSAIDs, including selective COX-2 inhibitors, with ACE inhibitors, including lisinopril, may result in deterioration of renal function, including possible acute renal failure.",
                      "In patients who are elderly, volume-depleted (including those on diuretic therapy), or with compromised renal function, coadministration of NSAIDs, including selective COX-2 inhibitors, with ACE inhibitors, including lisinopril, may result in deterioration of renal function, including possible acute renal failure."
                    ],
                    "pairs": [
                      {
                        "drug_a": "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:314076]",
                        "drug_b": "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain) [RXNORM:197806]"
                      }
                    ],
                    "grade": "Monitor"
                  }
                ]
              },
              {
                "severity": "ADJUST_DOSE",
                "label": "Adjust Dose",
                "items": []
              }
            ],
            "severe_effects": [
              {
                "drug": "Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet (active, Take 1 tablet by mouth every 6 hours as needed for pain., @ 2026-07-24, for Chronic pain) [RXNORM:856987]",
                "effects": [
                  {
                    "risk": "SERIOUS AND LIFE-THREATENING RISKS FROM USE OF HYDROCODONE BITARTRATE AND ACETAMINOPHEN TABLETS",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "LIFE-THREATENING RESPIRATORY DEPRESSION",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "ACCIDENTAL INGESTION",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "NEONATAL OPIOID WITHDRAWAL SYNDROME",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "HEPATOTOXICITY",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "RISK EVALUATION AND MITIGATION STRATEGY (REMS)",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "RISK EVALUATION AND MITIGATIONSTRATEGY (REMS)",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "SERIOUS AND LIFE-THREATENING RISKS FROM USE OF HYDRCODONE BITARTRATE AND ACETAMINOPHEN TABLETS",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Acetaminophen has been associated with cases of acute liver failure, at times resulting in liver transplant and death. Most of the cases of liver injury are associated with the use of acetaminophen at doses that exceed...",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "CYTOCHROME P450 3A4 INTERACTION and HEPATOTOXICITY",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "ADDICTION, ABUSE, AND MISUSE: RISK EVALUATION AND MITIGATION STRATEGY (REMS)",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "WITHDRAWAL SYNDROME",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "HEPATOTOXICITY and RISKS FROM CONCOMITANT USE WITH BENZODIAZEPINES OR OTHER CNS DEPRESSANTS",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "SERIOUS AND LIFE-THREATENING RISKS FROM USE OF HYDROCODONE BITARTRATE AND ACETAMINOPEHN TABLETS, USP",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "ACCIDENTAL",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Acetaminophen has been as s ociated with cas es of acute liver failure, at times res ulting in",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Contraindicated in Significant respiratory depression",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Acute or severe bronchial asthma in an unmonitored setting or in the absence of resuscitative equipment",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in hypersensitivity to other opioids",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated: Hypersensitivity to hydrocodone or acetaminophen (e.g., anaphylaxis)",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Known or suspected gastrointestinal obstruction, including paralytic ileus",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Hypersensitivity to acetaminophen",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Hypersensitivity to hydrocodone",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Known hypersensitivity to other opioids who may exhibit cross-sensitivity to hydrocodone",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in hypersensitivity to any other component of this product",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Hypersensitive to other opioids which may exhibit cross-sensitivity to hydrocodone",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Patients who have previously exhibited hypersensitivity to hydrocodone or acetaminophen",
                    "tier": "CONTRAINDICATION"
                  }
                ]
              },
              {
                "drug": "Ibuprofen 600 MG Oral Tablet (active, Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain., @ 2026-05-25, for Chronic pain) [RXNORM:197806]",
                "effects": [
                  {
                    "risk": "Cardiovascular Thrombotic Events",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "NSAIDs may cause an increased risk of serious cardiovascular thrombotic events, myocardial infarction, and stroke, which can be fatal. This risk may increase with duration of use. Patients with cardiovascular disease or...",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Cardiovascular Thrombolic Events",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Ibuprofen Tablets, USP Boxed Warnings",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Contraindicated in asthma, urticarial, or allergic-type reactions after taking aspirin or other NSAIDs",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Hypersensitivity to ibuprofen",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Preexisting Asthma",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in treatment of peri-operative pain in the setting of coronary artery bypass graft (CABG) surgery",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in patients with known hypersensitivity to ibuprofen",
                    "tier": "CONTRAINDICATION"
                  }
                ]
              },
              {
                "drug": "Alprazolam 0.5 MG Oral Tablet (active, Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety., @ 2026-03-26, for Severe anxiety (panic)) [RXNORM:308048]",
                "effects": [
                  {
                    "risk": "RISKS FROM CONCOMITANT USE WITH OPIOIDS",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "ABUSE, MISUSE, AND ADDICTION",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "DEPENDENCE AND WITHDRAWAL REACTIONS",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "AND",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Do not co-administer with itraconazole",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in known hypersensitivity to alprazolam or other benzodiazepines",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Do not co-administer with ketoconazole",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in patients with known sensitivity to this drug or other benzodiazepines",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in acute narrow angle glaucoma",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Do not co-administer with strong cytochrome P450 3A (CYP3A) inhibitors (e.g., ketoconazole, itraconazole)",
                    "tier": "CONTRAINDICATION"
                  }
                ]
              },
              {
                "drug": "topiramate 50 MG Oral Tablet (active, Take 1 tablet (50 mg) by mouth twice daily., @ 2024-01-25, for Chronic intractable migraine without aura) [RXNORM:151226]",
                "effects": [
                  {
                    "risk": "Contraindicated in patients with a history of hypersensitivity to any component of this product",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in patients with a history of hypersensitivity reaction to topiramate, topiramate tablets, or any of the inactive ingredients of topiramate tablets",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in history of hypersensitivity reaction to topiramate, TOPAMAX, or any of the inactive ingredients of TOPAMAX",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Hypersensitivity to topiramate tablets",
                    "tier": "CONTRAINDICATION"
                  }
                ]
              },
              {
                "drug": "lisinopril 10 MG Oral Tablet (active, Take 1 tablet (10 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:314076]",
                "effects": [
                  {
                    "risk": "• When pregnancy is detected, discontinue lisinopril tablets as soon as possible",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "WARNING: FETAL TOXICITY",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "When used in pregnancy during the second and third trimesters, ACE inhibitors can cause injury and even death to the developing fetus. When pregnancy is detected, lisinopril should be discontinued as soon as possible....",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "USE IN PREGNANCY",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "When pregnancy is detected, discontinue Lisinopril as soon as possible [see WARNINGS AND PRECAUTIONS (5.1)]",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "USE IN PREGNANCYWhen pregnancy is detected, lisinopril should be discontinued as soon as possible. See",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "used in pregnancy during the second and third trimesters, ACE",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Contraindicated in history of angioedema or hypersensitivity related to previous treatment with an angiotensin converting enzyme inhibitor",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Do not co-administer with aliskiren",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in hereditary or idiopathic angioedema",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in diabetes",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in patients who are hypersensitive to this product",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Do not co-administer with neprilysin inhibitor (e.g., sacubitril)",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in hypersensitivity to this product",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Do not co-administer with sacubitril/valsartan",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated: neprilysin inhibitor",
                    "tier": "CONTRAINDICATION"
                  }
                ]
              },
              {
                "drug": "24 HR metoprolol succinate 50 MG Extended Release Oral Tablet (active, Take 1 tablet (50 mg) by mouth once daily., @ 2023-12-01, for Peripartum cardiomyopathy) [RXNORM:866436]",
                "effects": [
                  {
                    "risk": "ISCHEMIC HEART DISEASE: Following abrupt cessation of therapy with certain beta-blocking agents, exacerbations of angina pectoris and, in some cases, myocardial infarction have occurred. When discontinuing chronically...",
                    "tier": "BOXED_WARNING"
                  },
                  {
                    "risk": "Contraindicated in decompensated heart failure",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in severe bradycardia",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in cardiogenic shock",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in sick sinus syndrome",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in patients who are hypersensitive to any component of this product",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in second or third degree heart block",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in decompensated cardiac failure",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in heart block greater than first degree",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Hypersensitivity to metoprolol",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in Severe peripheral arterial circulatory disorders",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated: other beta-blockers",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in systolic blood pressure < 100 mmHg",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in sinus bradycardia",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in moderate-to-severe cardiac failure",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in heart rate < 45 beats/min",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in significant first-degree heart block (P-R interval ≥ 0.24 sec)",
                    "tier": "CONTRAINDICATION"
                  },
                  {
                    "risk": "Contraindicated in overt cardiac failure",
                    "tier": "CONTRAINDICATION"
                  }
                ]
              }
            ],
            "corpus": "FDA DailyMed prescription drug labels (corpus snapshot full release 2026-08-14 (54,748 human-rx labels))"
          }
        }
      ],
      "presentation": {
        "density": "comfortable",
        "tone": "clinical"
      },
      "meta": {
        "render_hint": "cma",
        "response_shape": "safety_only",
        "protocol_version": "structured-output-v2",
        "section_count": 5,
        "preserve_section_titles": true,
        "tools_used": [
          "cma_route"
        ],
        "adk_clinical_facts_panel": true,
        "adk_facts_panel_title": "Facts & Evidences",
        "adk_patient_summary_panel_title": "Patient Summary",
        "adk_cma_sections_unrendered": []
      }
    },
    "rag_sources": [
      {
        "source": "Essential Geriatrics, 4th Edition (2022) [p. 80]",
        "number": "1",
        "doc_title": "Essential Geriatrics, 4th Edition (2022)",
        "chunk_id": "aea5b409c27c2c10ab2d714b0b04f5c0b1a65fa41a3c1a58460f470ebd7d53e0",
        "snippet": "ease interactions, and drugs without a clear indication would all be reasonable targets. Once the medication(s) for discontinuation is established, a decision needs to be taken whether abruptly stopping or slowly tapering down the dose is required. This latter approach would be e",
        "passage_text": "ease interactions, and drugs without a clear indication would all be reasonable targets. Once the medication(s) for discontinuation is established, a decision needs to be taken whether abruptly stopping or slowly tapering down the dose is required. This latter approach would be expected to reduce the risk of withdrawal effects. Deprescribing should be viewed as having a degree of trial and error; a ‘drug holiday’ is a way to describe it. Sometimes drugs will need to be restarted when symptoms recur or withdrawal effects are experienced. For these reasons, review and follow-up are important components. Good communication between the prescribers and deprescribers is required to ensure the process remains patient-centred and to minimise care fragmentation. As previously discussed, clinicians tend to overestimate the beneficial effects of medications, underesti­ mate the potential harms, assume patient adherence and overlook non-drug aspects of care. To help restore balanced decisions, future clinical guidelines should include deprescribing advice.150 Deprescribing is a multi-factorial, patient-specific, time-consuming and highly skilled process. It can’t be simplified to a list, guide",
        "document_type": "textbook",
        "section": "Essential Geriatrics, 4th Edition (2022) > p. 80",
        "page_start": 80,
        "page_end": 80,
        "_category": "safety",
        "_axis": "patient_factor"
      },
      {
        "source": "Oxford Handbook of Gastroenterology and Hepatology, 2nd Edition (2022) [pp. 533-534]",
        "number": "2",
        "doc_title": "Oxford Handbook of Gastroenterology and Hepatology, 2nd Edition (2022)",
        "chunk_id": "98f1590f7ec386969897bf332cc613be76eca0d36696b57add511f5a86a8e7bf",
        "snippet": "Paracentesis See: ➢ ‘Paracentesis’ (p. 177). Paracetamol (acetaminophen) overdose 495 Paracetamol (acetaminophen) overdose Background Paracetamol remains the commonest cause of drug overdose, and acute liver failure, in resource-​rich countries. Very safe at therapeutic levels (<",
        "passage_text": "Paracentesis See: ➢ ‘Paracentesis’ (p. 177). Paracetamol (acetaminophen) overdose 495 Paracetamol (acetaminophen) overdose Background Paracetamol remains the commonest cause of drug overdose, and acute liver failure, in resource-​rich countries. Very safe at therapeutic levels (<4g/d) as excreted via conjugation to non-​toxic glucuronate and sulphate metabolites. In overdose (particularly >0g), these pathways are saturated and a toxic metabolite, N-​acetyl-​p-​benzoquinone imine (NAPQI), is pro­ duced. This can be detoxified through a pathway dependent on glutathione, but once stores of this are exhausted, liver injury ensues. i toxicity asso­ ciated with chronic alcohol excess, malnutrition, staggered overdose, late presentation (>6h), and concomitant use of drugs that induce CYP450 (see % hepatic drug metabolism). Clinical features • Patients may be well, or have nausea/​vomiting for first 24h. • Liver failure, with progressive jaundice, hypoglycaemia, ▲ hepatic encephalopathy ▲ (often associated with cerebral oedema), and multiorgan failure develop from ≈48h post-​overdose. • Acute renal failure may occur independent of liver failure.",
        "document_type": "textbook",
        "section": "Oxford Handbook of Gastroenterology and Hepatology, 2nd Edition (2022) > pp. 533-534",
        "page_start": 533,
        "page_end": 534,
        "_category": "safety",
        "_axis": "adverse"
      },
      {
        "source": "2023 AHA-ACC-ACCP-ASPC-NLA-PCNA Guideline for the Management of Patients With Chronic Coronary Disease (2023) [p. 53]",
        "number": "17",
        "doc_title": "2023 AHA-ACC-ACCP-ASPC-NLA-PCNA Guideline for the Management of Patients With Chronic Coronary Disease (2023)",
        "chunk_id": "b1d5069ec22bdc15ebcf11c5fa2cab538a45bfae71bec5c360c0556869f3815f",
        "snippet": "4.3.2. Beta Blockers Recommendations for Beta Blockers Referenced studies that support the recommendations are summarized in the Online Data Supplement. COR LOE RECOMMENDATIONS 1. In patients with CCD and LVEF £40% with or without previous MI, the use of beta-blocker therapy is r",
        "passage_text": "4.3.2. Beta Blockers Recommendations for Beta Blockers Referenced studies that support the recommendations are summarized in the Online Data Supplement. COR LOE RECOMMENDATIONS 1. In patients with CCD and LVEF £40% with or without previous MI, the use of beta-blocker therapy is recommended to reduce the risk of future MACE, including cardiovascular death.1-3 1 A 2. In patients with CCD and LVEF <50%, the use of sustained release metoprolol succinate, carvedilol, or bisoprolol with titration to target doses is recommended in preference to other beta blockers.*1,3-8 1 A 3. In patients with CCD who were initiated on beta-blocker therapy for previous MI without a history of or current LVEF £50%, angina, arrhythmias, or uncontrolled hypertension, it may be reasonable to reassess the indication for long-term (>1 year) use of beta-blocker therapy for reducing MACE.9-15 2b B-NR",
        "document_type": "guideline",
        "section": "2023 AHA-ACC-ACCP-ASPC-NLA-PCNA Guideline for the Management of Patients With Chronic Coronary Disease (2023) > p. 53",
        "page_start": 53,
        "page_end": 53,
        "_category": "safety",
        "_axis": "adverse_2"
      }
    ],
    "guideline_evidence": {
      "categories": [
        {
          "category": "safety",
          "answer": "",
          "evidence_quality": "strong",
          "source_numbers": [
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "9",
            "10",
            "11",
            "13",
            "14",
            "15",
            "16",
            "17",
            "18",
            "19",
            "20",
            "21",
            "22"
          ]
        }
      ],
      "references": [
        {
          "doc_title": "Essential Geriatrics, 4th Edition (2022)",
          "document_type": "textbook",
          "passages": [
            {
              "category": "safety",
              "number": "1",
              "snippet": "ease interactions, and drugs without a clear indication would all be reasonable targets. Once the medication(s) for discontinuation is established, a decision needs to be taken whether abruptly stopping or slowly tapering down the dose is required. This latter approach would be e",
              "passage_text": "ease interactions, and drugs without a clear indication would all be reasonable targets. Once the medication(s) for discontinuation is established, a decision needs to be taken whether abruptly stopping or slowly tapering down the dose is required. This latter approach would be expected to reduce the risk of withdrawal effects. Deprescribing should be viewed as having a degree of trial and error; a ‘drug holiday’ is a way to describe it. Sometimes drugs will need to be restarted when symptoms recur or withdrawal effects are experienced. For these reasons, review and follow-up are important components. Good communication between the prescribers and deprescribers is required to ensure the process remains patient-centred and to minimise care fragmentation. As previously discussed, clinicians tend to overestimate the beneficial effects of medications, underesti­ mate the potential harms, assume patient adherence and overlook non-drug aspects of care. To help restore balanced decisions, future clinical guidelines should include deprescribing advice.150 Deprescribing is a multi-factorial, patient-specific, time-consuming and highly skilled process. It can’t be simplified to a list, guide",
              "document_type": "textbook",
              "section": "Essential Geriatrics, 4th Edition (2022) > p. 80",
              "page_start": 80,
              "page_end": 80
            }
          ]
        },
        {
          "doc_title": "Oxford Handbook of Gastroenterology and Hepatology, 2nd Edition (2022)",
          "document_type": "textbook",
          "passages": [
            {
              "category": "safety",
              "number": "2",
              "snippet": "Paracentesis See: ➢ ‘Paracentesis’ (p. 177). Paracetamol (acetaminophen) overdose 495 Paracetamol (acetaminophen) overdose Background Paracetamol remains the commonest cause of drug overdose, and acute liver failure, in resource-​rich countries. Very safe at therapeutic levels (<",
              "passage_text": "Paracentesis See: ➢ ‘Paracentesis’ (p. 177). Paracetamol (acetaminophen) overdose 495 Paracetamol (acetaminophen) overdose Background Paracetamol remains the commonest cause of drug overdose, and acute liver failure, in resource-​rich countries. Very safe at therapeutic levels (<4g/d) as excreted via conjugation to non-​toxic glucuronate and sulphate metabolites. In overdose (particularly >0g), these pathways are saturated and a toxic metabolite, N-​acetyl-​p-​benzoquinone imine (NAPQI), is pro­ duced. This can be detoxified through a pathway dependent on glutathione, but once stores of this are exhausted, liver injury ensues. i toxicity asso­ ciated with chronic alcohol excess, malnutrition, staggered overdose, late presentation (>6h), and concomitant use of drugs that induce CYP450 (see % hepatic drug metabolism). Clinical features • Patients may be well, or have nausea/​vomiting for first 24h. • Liver failure, with progressive jaundice, hypoglycaemia, ▲ hepatic encephalopathy ▲ (often associated with cerebral oedema), and multiorgan failure develop from ≈48h post-​overdose. • Acute renal failure may occur independent of liver failure.",
              "document_type": "textbook",
              "section": "Oxford Handbook of Gastroenterology and Hepatology, 2nd Edition (2022) > pp. 533-534",
              "page_start": 533,
              "page_end": 534
            }
          ]
        }
      ],
      "review_incomplete": false,
      "retrieval_quality": {}
    }
  },
  {
    "id": "what-is-the-trend-in-her-blood-pressure",
    "question": "What is the trend in her blood pressure?",
    "answer": "There are 86 dated values on record across 2 measurements. Systolic Blood Pressure has fluctuated with no steady trend, from a first value of 94 mm[Hg] on 1987-10-02 to a latest value of 93 mm[Hg] on 2024-12-20. While values remained largely within a similar range for decades, a transient period of elevation was noted in late 2023 (peaking at 151 mm[Hg] on 2023-10-06) before returning to 93 mm[Hg]. Diastolic Blood Pressure has fluctuated with no steady trend, starting at 68 mm[Hg] on 1987-10-02 and reaching a latest value of 70 mm[Hg] on 2024-12-20. The pattern includes a transient increase in late 2023 (peaking at 97 mm[Hg] on 2023-10-06) followed by a return to 70 mm[Hg].\n\nFor complete information, check the ***Facts & Evidences*** card below.",
    "intent_type": "data_query",
    "tools_used": [
      "get_observations"
    ],
    "processing_time_ms": 7772,
    "thinking_event_count": 25,
    "suggestions": [
      "Are there recent trends in her heart rate to compare with the blood pressure?",
      "How do these blood pressure readings compare to her baseline on lisinopril?",
      "What was the most recent serum creatinine or BUN level recorded?"
    ],
    "structured": {
      "response_type": "narrative_summary_card",
      "title": "Repeated measurements with trend interpretation",
      "subtitle": "Sequence-based clinical trend review",
      "sections": [
        {
          "type": "paragraph",
          "title": "Systolic Blood Pressure — interpretation",
          "content": "**Systolic Blood Pressure** (LOINC: 8480-6)\n\nValues reviewed: 43 (1987-10-02 to 2024-12-20)\nSystolic Blood Pressure has fluctuated with no steady trend, from a first value of 94 mm[Hg] on 1987-10-02 to a latest value of 93 mm[Hg] on 2024-12-20. While values remained largely within a similar range for decades, a transient period of elevation was noted in late 2023 (peaking at 151 mm[Hg] on 2023-10-06) before returning to 93 mm[Hg].",
          "meta": {
            "block_type": "TREND_CANDIDATE_SUMMARY",
            "trend_candidate_summary": true
          },
          "presentation": {
            "layout": "narrative",
            "density": "comfortable",
            "font_scale": "md",
            "separators": true
          }
        },
        {
          "type": "table",
          "title": "Systolic Blood Pressure — chronological values",
          "columns": [
            "Date",
            "Value"
          ],
          "rows": [
            {
              "date": "1987-10-02",
              "value": "94 mmHg"
            },
            {
              "date": "1987-11-06",
              "value": "96 mmHg"
            },
            {
              "date": "1988-01-08",
              "value": "96 mmHg"
            },
            {
              "date": "1988-03-11",
              "value": "95 mmHg"
            },
            {
              "date": "1988-06-10",
              "value": "96 mmHg"
            },
            {
              "date": "1988-09-09",
              "value": "96 mmHg"
            },
            {
              "date": "1988-12-09",
              "value": "96 mmHg"
            },
            {
              "date": "1989-03-10",
              "value": "93 mmHg"
            },
            {
              "date": "1989-09-08",
              "value": "93 mmHg"
            },
            {
              "date": "1990-03-09",
              "value": "94 mmHg"
            },
            {
              "date": "1990-09-07",
              "value": "102 mmHg"
            },
            {
              "date": "1991-03-08",
              "value": "94 mmHg"
            },
            {
              "date": "1991-09-06",
              "value": "94 mmHg"
            },
            {
              "date": "1992-09-11",
              "value": "90 mmHg"
            },
            {
              "date": "1993-09-17",
              "value": "95 mmHg"
            },
            {
              "date": "1994-09-23",
              "value": "94 mmHg"
            },
            {
              "date": "1995-09-29",
              "value": "97 mmHg"
            },
            {
              "date": "1996-10-04",
              "value": "101 mmHg"
            },
            {
              "date": "1997-10-10",
              "value": "95 mmHg"
            },
            {
              "date": "1998-10-16",
              "value": "93 mmHg"
            },
            {
              "date": "1999-10-22",
              "value": "97 mmHg"
            },
            {
              "date": "2000-03-10",
              "value": "93 mmHg"
            },
            {
              "date": "2000-10-27",
              "value": "90 mmHg"
            },
            {
              "date": "2001-11-02",
              "value": "101 mmHg"
            },
            {
              "date": "2002-11-08",
              "value": "86 mmHg"
            },
            {
              "date": "2003-11-14",
              "value": "99 mmHg"
            },
            {
              "date": "2004-11-19",
              "value": "93 mmHg"
            },
            {
              "date": "2005-11-25",
              "value": "98 mmHg"
            },
            {
              "date": "2006-12-01",
              "value": "99 mmHg"
            },
            {
              "date": "2009-12-04",
              "value": "100 mmHg"
            },
            {
              "date": "2010-03-26",
              "value": "88 mmHg"
            },
            {
              "date": "2012-04-06",
              "value": "99 mmHg"
            },
            {
              "date": "2012-12-07",
              "value": "94 mmHg"
            },
            {
              "date": "2015-12-11",
              "value": "100 mmHg"
            },
            {
              "date": "2016-04-15",
              "value": "95 mmHg"
            },
            {
              "date": "2018-12-14",
              "value": "99 mmHg"
            },
            {
              "date": "2019-02-01",
              "value": "89 mmHg"
            },
            {
              "date": "2021-12-17",
              "value": "101 mmHg"
            },
            {
              "date": "2023-09-15",
              "value": "142 mmHg"
            },
            {
              "date": "2023-09-29",
              "value": "146 mmHg"
            },
            {
              "date": "2023-10-06",
              "value": "151 mmHg"
            },
            {
              "date": "2023-11-24",
              "value": "148 mmHg"
            },
            {
              "date": "2024-12-20",
              "value": "93 mmHg"
            }
          ],
          "meta": {
            "block_type": "TABLE_BLOCK",
            "trend_value_sequence": true
          },
          "presentation": {
            "layout": "table",
            "density": "comfortable",
            "font_scale": "sm",
            "separators": true
          }
        },
        {
          "type": "paragraph",
          "title": "Diastolic Blood Pressure — interpretation",
          "content": "**Diastolic Blood Pressure** (LOINC: 8462-4)\n\nValues reviewed: 43 (1987-10-02 to 2024-12-20)\nDiastolic Blood Pressure has fluctuated with no steady trend, starting at 68 mm[Hg] on 1987-10-02 and reaching a latest value of 70 mm[Hg] on 2024-12-20. The pattern includes a transient increase in late 2023 (peaking at 97 mm[Hg] on 2023-10-06) followed by a return to 70 mm[Hg].",
          "meta": {
            "block_type": "TREND_CANDIDATE_SUMMARY",
            "trend_candidate_summary": true
          },
          "presentation": {
            "layout": "narrative",
            "density": "comfortable",
            "font_scale": "md",
            "separators": true
          }
        },
        {
          "type": "table",
          "title": "Diastolic Blood Pressure — chronological values",
          "columns": [
            "Date",
            "Value"
          ],
          "rows": [
            {
              "date": "1987-10-02",
              "value": "68 mmHg"
            },
            {
              "date": "1987-11-06",
              "value": "66 mmHg"
            },
            {
              "date": "1988-01-08",
              "value": "66 mmHg"
            },
            {
              "date": "1988-03-11",
              "value": "67 mmHg"
            },
            {
              "date": "1988-06-10",
              "value": "69 mmHg"
            },
            {
              "date": "1988-09-09",
              "value": "70 mmHg"
            },
            {
              "date": "1988-12-09",
              "value": "62 mmHg"
            },
            {
              "date": "1989-03-10",
              "value": "63 mmHg"
            },
            {
              "date": "1989-09-08",
              "value": "69 mmHg"
            },
            {
              "date": "1990-03-09",
              "value": "63 mmHg"
            },
            {
              "date": "1990-09-07",
              "value": "67 mmHg"
            },
            {
              "date": "1991-03-08",
              "value": "70 mmHg"
            },
            {
              "date": "1991-09-06",
              "value": "70 mmHg"
            },
            {
              "date": "1992-09-11",
              "value": "70 mmHg"
            },
            {
              "date": "1993-09-17",
              "value": "68 mmHg"
            },
            {
              "date": "1994-09-23",
              "value": "70 mmHg"
            },
            {
              "date": "1995-09-29",
              "value": "64 mmHg"
            },
            {
              "date": "1996-10-04",
              "value": "67 mmHg"
            },
            {
              "date": "1997-10-10",
              "value": "70 mmHg"
            },
            {
              "date": "1998-10-16",
              "value": "68 mmHg"
            },
            {
              "date": "1999-10-22",
              "value": "66 mmHg"
            },
            {
              "date": "2000-03-10",
              "value": "65 mmHg"
            },
            {
              "date": "2000-10-27",
              "value": "68 mmHg"
            },
            {
              "date": "2001-11-02",
              "value": "68 mmHg"
            },
            {
              "date": "2002-11-08",
              "value": "67 mmHg"
            },
            {
              "date": "2003-11-14",
              "value": "65 mmHg"
            },
            {
              "date": "2004-11-19",
              "value": "68 mmHg"
            },
            {
              "date": "2005-11-25",
              "value": "63 mmHg"
            },
            {
              "date": "2006-12-01",
              "value": "60 mmHg"
            },
            {
              "date": "2009-12-04",
              "value": "70 mmHg"
            },
            {
              "date": "2010-03-26",
              "value": "68 mmHg"
            },
            {
              "date": "2012-04-06",
              "value": "70 mmHg"
            },
            {
              "date": "2012-12-07",
              "value": "64 mmHg"
            },
            {
              "date": "2015-12-11",
              "value": "68 mmHg"
            },
            {
              "date": "2016-04-15",
              "value": "70 mmHg"
            },
            {
              "date": "2018-12-14",
              "value": "64 mmHg"
            },
            {
              "date": "2019-02-01",
              "value": "62 mmHg"
            },
            {
              "date": "2021-12-17",
              "value": "67 mmHg"
            },
            {
              "date": "2023-09-15",
              "value": "92 mmHg"
            },
            {
              "date": "2023-09-29",
              "value": "94 mmHg"
            },
            {
              "date": "2023-10-06",
              "value": "97 mmHg"
            },
            {
              "date": "2023-11-24",
              "value": "96 mmHg"
            },
            {
              "date": "2024-12-20",
              "value": "70 mmHg"
            }
          ],
          "meta": {
            "block_type": "TABLE_BLOCK",
            "trend_value_sequence": true
          },
          "presentation": {
            "layout": "table",
            "density": "comfortable",
            "font_scale": "sm",
            "separators": true
          }
        }
      ],
      "presentation": {
        "density": "comfortable",
        "font_scale": "md",
        "layout": "mixed",
        "variant": "summary_narrative_card",
        "tone": "clinical",
        "emphasis": "data-first",
        "body_markdown_visible": true,
        "theme": "clinical_dark"
      },
      "meta": {
        "render_hint": "labs",
        "summary_narrative_card": true,
        "structured_body_authoritative": true,
        "body_markdown_visible": true,
        "trend_interpretation_card": true,
        "trend_candidate_blocks": true,
        "protocol_version": "structured-output-v2",
        "adk_clinical_facts_panel": true,
        "adk_facts_panel_title": "Facts & Evidences",
        "adk_trend_viz_targets": [
          {
            "item_id": "vital:8480-6@mm[Hg]",
            "label": "Systolic Blood Pressure",
            "code": "8480-6",
            "unit": "mm[Hg]"
          },
          {
            "item_id": "vital:8462-4@mm[Hg]",
            "label": "Diastolic Blood Pressure",
            "code": "8462-4",
            "unit": "mm[Hg]"
          }
        ]
      }
    }
  }
];
